import { cn } from '../../lib/cn.js'
import { NOT_RECORDED } from '../../lib/words.js'

export interface WordListProps {
  words: string[] | null | undefined
  /** What to say when the list is empty. Never a blank. */
  empty?: string
  className?: string
}

/**
 * A list of words a record holds, **read rather than pressed**.
 *
 * Allergies, medications and conditions on the break-glass health record; the
 * languages a professional speaks; what they take on. The API sends an array of
 * strings and the desk shows it.
 *
 * Neither of the two things that look right is right. `Chip` is *"a
 * narrow-it-down control that is pressed rather than read"* and says in its own
 * comment why that matters — somebody who learns a coloured pill is a button will
 * press one. `Badge` is a status word about a whole row, with a tone; an allergy
 * is not a status and colouring it says something nobody meant.
 *
 * So: plain, separated, and inert. On a health record that is the correct
 * register — the screen is being read by somebody deciding something, and a row
 * of pressable pills invites them to click their way through a person's
 * medications.
 *
 * `empty` is a sentence, not a blank. On that record the difference between "no
 * allergies recorded" and an empty cell is the difference between a fact and a
 * missing answer.
 */
export function WordList({ words, empty = NOT_RECORDED, className }: WordListProps) {
  if (!words || words.length === 0) {
    return <span className={cn('text-ink-muted', className)}>{empty}</span>
  }
  return (
    <ul className={cn('flex flex-wrap gap-x-2 gap-y-1', className)}>
      {words.map((word, i) => (
        <li key={word} className="text-body text-ink">
          {word}
          {/* A separator that is drawn, not typed into the text, so copying the
              list does not carry stray bullets into an email. */}
          {i < words.length - 1 && (
            <span className="ml-2 text-hairline-strong" aria-hidden>
              ·
            </span>
          )}
        </li>
      ))}
    </ul>
  )
}
